/**
 * Tailwind class strings that more than one page needs, kept here rather than as
 * CSS component classes so the markup still shows what it renders. Mirrors the
 * company reference dashboard, which exports its pill and link classes from
 * `lib/` the same way.
 *
 * What is left after PRs B and C is layout and typography that no primitive
 * owns: an eyebrow, a title row, a stacked field label, the two code
 * treatments (one block, one inline) and the small metric pill. The hand-rolled
 * card surface is gone — every panel in the app is a `components/ui/card` now.
 */

/** Small uppercase label above a heading: 12px, the scale's floor (#163 R4). */
export const EYEBROW = "text-xs font-semibold tracking-[0.08em] uppercase text-muted-foreground";

/** Header row inside a panel, or a page's title row: content split left/right. */
export const TOOLBAR = "flex flex-wrap items-center justify-between gap-4";

/** Two-column term/value list: terms read muted on the left, values right. */
export const DEFINITION_LIST =
  "grid grid-cols-[1fr_auto] gap-2 [&>dd]:m-0 [&>dt]:text-muted-foreground";

/**
 * Stacked label + control, as `<label>Text<input /></label>`: a 12px, weight
 * 500 label in the foreground colour, 4px above its control (#163 R4, R5).
 */
export const FIELD_LABEL = "grid gap-1 text-xs font-medium text-foreground";

/**
 * Light code surface for transcripts, payloads, document viewers and build
 * logs (#162 A5): a muted panel with a hairline border, no radius, the page's
 * own text colour and the surrounding font size. Lines keep their shape —
 * `whitespace-pre` scrolls sideways instead of breaking a token. Block only —
 * it belongs on a `<pre>`, never on an inline `<code>`: block padding on an
 * inline box paints outside the line box and overlaps the lines above and
 * below it (#118). Inline code uses `INLINE_CODE`.
 */
export const CODE_SURFACE =
  "overflow-auto border bg-muted/50 px-2 py-1.5 whitespace-pre text-foreground";

/**
 * Compact chip for an inline `<code>` inside running text, a table cell or a
 * control label. Horizontal padding only, so the chip stays inside the line box
 * it shares with the surrounding words, and `align-baseline` keeps it sitting
 * on their baseline. It sets the code family (#175: JetBrains Mono inside Mona
 * Sans text) but no size of its own, so the chip inherits the size of the text
 * it sits in and never drops below the 12px floor inside a `text-xs` cell
 * (#163 R4). Square, like every surface (#163 R2), and `overflow-wrap: anywhere`
 * lets a long token break rather than widen the page.
 */
export const INLINE_CODE =
  "bg-muted px-1 align-baseline font-mono text-foreground [overflow-wrap:anywhere]";

/** Square tag for a small inline metric: 12px, bordered, muted. */
export const CHIP =
  "inline-flex items-center gap-1.5 border bg-muted/50 px-2 py-1 text-xs whitespace-nowrap text-muted-foreground";
